import type { NamedArgs, PositionalArgs } from 'ember-modifier';
import Modifier from 'ember-modifier';

import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { capitalize } from '@ember/string';

// Props to https://github.com/jelhan/ember-autoresize-modifier

type IMode = 'height' | 'width';

interface IAutoResizeSignature {
  Element: HTMLElement;
  Args: {
    Named: { mode?: IMode };
    Positional: [string, boolean | undefined];
  };
}

export default class AutoresizeModifier extends Modifier<IAutoResizeSignature> {
  el: HTMLElement | null = null;
  disabled = false;
  mode?: IMode;
  value?: string;

  modify(
    element: HTMLElement,
    [value, disabled]: PositionalArgs<IAutoResizeSignature>,
    { mode }: NamedArgs<IAutoResizeSignature>
  ): void {
    this.el = element;
    this.mode = mode;
    this.value = value;
    this.disabled = disabled ?? false;

    if (!this.disabled) {
      // resize on every input event
      this.el.addEventListener('input', this.scheduleResize);
    }

    this.scheduleResize();
    registerDestructor(this, () => {
      this.el?.removeEventListener('input', this.scheduleResize);
    });
  }

  @action
  resize() {
    if (!this.el) {
      return;
    }
    const { el, mode = 'height' } = this;

    const previousWrap = el.style.whiteSpace;

    if (this.mode === 'width') {
      // disable default wrapping
      el.style.whiteSpace = 'pre';
    }

    const capitalizeDimension = capitalize(mode);

    // height / width must be calculated independently from height / width previously enforced
    el.style[mode] = 'auto';

    const isBorderBox = window.getComputedStyle(el).boxSizing === 'border-box';
    let requiredDimension = el[`scroll${capitalizeDimension}` as keyof HTMLElement] as number;

    if (isBorderBox) {
      // borders must be added on top of scrollHeight / scrollWidth if box-sizing is border-box
      const borderDimension =
        (el[`offset${capitalizeDimension}` as keyof HTMLElement] as number) -
        (el[`client${capitalizeDimension}` as keyof HTMLElement] as number);
      requiredDimension += borderDimension;
    }

    el.style[mode] = `${requiredDimension}px`;

    el.style.whiteSpace = previousWrap;
  }

  @action
  scheduleResize() {
    // eslint-disable-next-line ember/no-runloop
    scheduleOnce('afterRender', this, this.resize);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    autoresize: typeof AutoresizeModifier;
  }
}
